import { useEffect, useState } from "react";
import { apiTech } from "@/lib/api";
import { useRealtime } from "@/lib/realtime";
import { EmptyState, StatusPill } from "@/components/admin";
import { ChevronRight, MapPin, Clock } from "lucide-react";

export default function TechSchedule({ onOpenJob }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = () => apiTech.get("/tech/jobs/scheduled")
    .then((r) => setJobs(r.data))
    .catch(() => {})
    .finally(() => setLoading(false));
  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useRealtime("tech", ["job."], reload);

  const groups = groupByDay(jobs);

  return (
    <>
      <div className="text-[11px] uppercase tracking-wider text-[#FFFFFF]/70 font-medium pt-1 pb-1">
        Upcoming <span className="text-[#FFFFFF]/45 num">· {jobs.length}</span>
      </div>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="h-24 bg-[#002060] border border-[#FFFFFF]/15 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : jobs.length === 0 ? (
        <div className="bg-[#002060] border border-[#FFFFFF]/15 rounded-lg">
          <EmptyState message="Nothing on the books yet." />
        </div>
      ) : (
        groups.map(([day, items]) => (
          <section key={day} className="mt-3 first:mt-0">
            <div className="text-[11px] uppercase tracking-wider text-[#FFFFFF]/70 font-semibold py-1.5">
              {day} <span className="text-[#FFFFFF]/45 num">· {items.length}</span>
            </div>
            <ul className="space-y-2">
              {items.map((j) => (
                <li key={j.id}>
                  <button
                    type="button"
                    onClick={() => onOpenJob(j.id)}
                    data-testid={`tech-sched-${j.id}`}
                    className="w-full text-left bg-[#002060] border border-[#FFFFFF]/15 rounded-lg p-3.5 hover:border-[#FF6600] active:scale-[0.99] transition-all"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold capitalize text-sm">{j.appliance}</span>
                          <StatusPill status={j.status} />
                        </div>
                        <div className="text-sm text-[#FFFFFF] mt-0.5 line-clamp-1">{j.customer_name}</div>
                        <div className="flex items-start gap-1.5 text-xs text-[#FFFFFF]/70 mt-1">
                          <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
                          <span className="line-clamp-1">{j.address}</span>
                        </div>
                        {j.scheduled_for && (
                          <div className="flex items-center gap-1.5 text-xs text-[#FFFFFF]/70 mt-0.5">
                            <Clock className="w-3 h-3" /> {prettyTime(j.scheduled_for)}
                          </div>
                        )}
                      </div>
                      <ChevronRight className="w-4 h-4 text-[#FFFFFF]/45 shrink-0 mt-1" />
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </>
  );
}

// Group jobs by readable day label, sorted by scheduled_for ascending.
function groupByDay(jobs) {
  const fmt = new Intl.DateTimeFormat(undefined, { weekday: "short", month: "short", day: "numeric" });
  const buckets = new Map();
  const undated = [];
  for (const j of jobs) {
    const d = parseDate(j.scheduled_for);
    if (!d) { undated.push(j); continue; }
    const label = fmt.format(d);
    if (!buckets.has(label)) buckets.set(label, []);
    buckets.get(label).push(j);
  }
  // Sort each bucket by scheduled_for
  for (const arr of buckets.values()) arr.sort((a, b) => (a.scheduled_for || "").localeCompare(b.scheduled_for || ""));
  const out = Array.from(buckets.entries());
  if (undated.length) out.push(["Unscheduled", undated]);
  return out;
}

function parseDate(s) {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.valueOf()) ? null : d;
}

function prettyTime(s) {
  const d = parseDate(s);
  if (!d) return s;
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}
